import { useEffect, useRef } from 'react'
import { registerPrivilegesPaymentHost } from './usePrivilegesPayment'
import { useAppSelector } from '../../app/hooks'
import { isEmbeddedInTodayWork } from '../../utils/embedMode'
import { publishTodayWorkQuota } from '../../utils/todayWorkQuota'
import {
  buildTodayWorkPrivilegesReply,
  readTodayWorkPrivilegesRequest,
  TODAY_WORK_PRIVILEGES_FRAME,
  TodayWorkPrivilegesRequest
} from '../../utils/todayWorkPrivilegesProtocol'

interface TodayWorkPrivilegesBridgeProps {
  /** 应用已完成当前团队的初始化。Suspense 保证路由内容也已挂载。 */
  ready: boolean
  /** 登录或团队承接失败的原因；空字符串表示没有已确认的错误。 */
  error: string
}

/**
 * 由 App 的路由 Suspense 内挂载，给客户端权益页报告真实就绪/失败状态。
 * 仅专用 iframe 启用；window.name 在站内整页跳转后保留，发票/返回权益等跳转也能重新握手。
 * @param props - 应用初始化与错误状态
 * @returns 不渲染额外界面
 */
export function TodayWorkPrivilegesBridge({
  ready,
  error
}: TodayWorkPrivilegesBridgeProps): null {
  const userId = useAppSelector((state) => state.main.user?.user_id ?? '')
  const groupId = useAppSelector((state) => state.group.groupId ?? '')
  const current = useRef({ ready, error, userId, groupId })
  current.current = { ready, error, userId, groupId }
  const request = useRef<TodayWorkPrivilegesRequest | null>(null)

  const publish = () => {
    if (!request.current) return
    const reply = buildTodayWorkPrivilegesReply(
      request.current,
      current.current
    )
    publishTodayWorkQuota(
      reply.status === 'ready' ? request.current.quota : undefined
    )
    // app:// 在 Electron 中可能序列化为 null；只回复已校验的直接父窗。
    window.parent.postMessage(reply, '*')
  }
  const publishRef = useRef(publish)
  publishRef.current = publish

  useEffect(() => {
    if (!isEmbeddedInTodayWork() || window.name !== TODAY_WORK_PRIVILEGES_FRAME)
      return
    const unregisterPayment = registerPrivilegesPaymentHost(
      (vipKind, userId, groupId) => {
        const host = request.current
        if (!host?.openMembershipSupported) return 'unsupported'
        if (
          ![1, 2, 3, 4].includes(vipKind) ||
          host.userId !== userId ||
          host.groupId !== groupId ||
          buildTodayWorkPrivilegesReply(host, current.current).status !==
            'ready'
        )
          return 'unavailable'
        window.parent.postMessage(
          { ...host, type: 'open-membership', vipKind },
          '*'
        )
        return 'opened'
      }
    )
    const receive = (event: MessageEvent) => {
      const message = readTodayWorkPrivilegesRequest(event, window.parent)
      if (!message) return
      request.current = message
      publishRef.current()
    }
    window.addEventListener('message', receive)
    return () => {
      unregisterPayment()
      request.current = null
      publishTodayWorkQuota(undefined)
      window.removeEventListener('message', receive)
    }
  }, [])

  useEffect(() => publishRef.current(), [ready, error, userId, groupId])
  return null
}
